import React, { useState, useRef, useEffect } from 'react';
import { AOTCharacter, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { Search } from 'lucide-react';

interface AutocompleteProps {
    characters: AOTCharacter[];
    onSelect: (character: AOTCharacter) => void;
    placeholder: string;
    language?: Language;
}

export default function Autocomplete({ characters, onSelect, placeholder, language = 'en' }: AutocompleteProps) {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const t = TRANSLATIONS[language];

    const filteredCharacters = characters.filter(c =>
        c.name.toLowerCase().startsWith(query.toLowerCase())
    ).slice(0, 10);

    useEffect(() => {
        setHighlightedIndex(0);
    }, [query]);

    const handleSelect = (character: AOTCharacter) => {
        onSelect(character);
        setQuery('');
        setIsOpen(false);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen || filteredCharacters.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredCharacters.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredCharacters.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredCharacters[highlightedIndex]) {
                    handleSelect(filteredCharacters[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                break;
        }
    };

    const showResults = isOpen && query.length > 0 && filteredCharacters.length > 0;
    const showNoResults = isOpen && query.length > 0 && filteredCharacters.length === 0;
    const listboxId = 'character-search-listbox';

    return (
        <div className="w-full max-w-xl mx-auto relative">
            {/* Screen reader announcements */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
            >
                {showResults && `${filteredCharacters.length} ${t.results_found || 'characters found'}`}
                {showNoResults && t.no_results}
            </div>

            <div className="relative">
                <Search
                    className="absolute left-5 top-1/2 -translate-y-1/2 text-[#6b9b7a] pointer-events-none z-10"
                    size={22}
                    aria-hidden="true"
                />
                <input
                    ref={inputRef}
                    type="text"
                    id="character-search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showResults}
                    aria-controls={listboxId}
                    aria-activedescendant={showResults ? `character-option-${highlightedIndex}` : undefined}
                    aria-describedby="search-instructions"
                    aria-label={placeholder}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onBlur={() => setTimeout(() => setIsOpen(false), 300)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="w-full bg-[#0d0d0d]/95 border-2 border-[#4a7c59]/40 text-[#e8e4db] pl-14 pr-5 py-4 text-lg rounded-xl focus:outline-none focus:border-[#4a7c59] focus:ring-2 focus:ring-[#4a7c59]/20 transition-all duration-300 placeholder:text-stone-500"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                />
                <span id="search-instructions" className="sr-only">
                    {t.search_instructions || 'Type to search characters. Use arrow keys to navigate results, Enter to select.'}
                </span>
            </div>

            {showResults && (
                <div
                    ref={listRef}
                    id={listboxId}
                    role="listbox"
                    aria-label={t.character_results || 'Character search results'}
                    className="absolute left-0 right-0 mt-3 bg-[#141414] border-2 border-[#4a7c59]/40 rounded-xl shadow-2xl z-50 overflow-hidden max-h-80 overflow-y-auto"
                >
                    {filteredCharacters.map((character, index) => (
                        <div
                            key={character.id}
                            id={`character-option-${index}`}
                            role="option"
                            aria-selected={index === highlightedIndex}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(character);
                            }}
                            className={`flex items-center gap-4 px-5 py-4 cursor-pointer transition-all duration-200 border-b border-[#4a7c59]/10 last:border-b-0
                                ${index === highlightedIndex ? 'bg-[#4a7c59]/25' : 'hover:bg-[#4a7c59]/15'}`}
                        >
                            <img
                                src={character.image}
                                alt=""
                                aria-hidden="true"
                                className="w-12 h-12 rounded-full object-cover object-top border-2 border-[#4a7c59]/60 shadow-md flex-shrink-0"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/images/placeholder.webp';
                                }}
                            />
                            <div className="flex-1 min-w-0 overflow-hidden">
                                <p className="font-semibold text-white text-base truncate">{character.name}</p>
                                <p className="text-sm text-[#6b9b7a] truncate" aria-label={`Affiliation: ${character.affiliation}`}>{character.affiliation}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showNoResults && (
                <div role="status" className="absolute left-0 right-0 mt-3 bg-[#141414] border-2 border-[#4a7c59]/40 rounded-xl p-6 text-center text-[#888] shadow-xl z-50">
                    {t.no_results}
                </div>
            )}
        </div>
    );
}
